import {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { cn } from '@utils/cn';

import {
  getSliderRootStyle,
  sliderItemClasses,
  sliderRootClasses,
  sliderTrackClasses
} from './Slider.styles';
import type { SliderProps, SliderRef } from './Slider.types';

const DRAG_THRESHOLD_PX = 5;
const GAP_PX = 8;

function getItemWidth(containerWidth: number, visible: number): number {
  return (containerWidth + GAP_PX) / visible;
}

export const Slider = forwardRef<SliderRef, SliderProps>(
  ({ children, desktopVisible = 3, mobileVisible = 1, className, onSlideChange }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef({ startX: 0, startScrollLeft: 0, hasMoved: false });
    const totalItems = useMemo(() => Children.toArray(children), [children]).length;

    const rootStyle = getSliderRootStyle(desktopVisible, mobileVisible);

    const getVisibleCount = useCallback(
      () => (window.innerWidth < 992 ? mobileVisible : desktopVisible),
      [mobileVisible, desktopVisible]
    );

    const getLastSlide = useCallback(
      () => Math.max(0, totalItems - getVisibleCount()),
      [totalItems, getVisibleCount]
    );

    const smoothScrollToSlide = useCallback(
      (slide: number) => {
        const el = containerRef.current;
        if (el) {
          const visible = getVisibleCount();
          const itemWidth = getItemWidth(el.clientWidth, visible);
          el.scrollTo({ left: itemWidth * slide, behavior: 'smooth' });
        }
      },
      [getVisibleCount]
    );

    const notifySlideChange = useCallback(
      (slide: number) => {
        const lastSlide = getLastSlide();
        onSlideChange?.(slide, { isFirst: slide === 0, isLast: slide >= lastSlide });
      },
      [onSlideChange, getLastSlide]
    );

    const goToSlide = useCallback(
      (slide: number) => {
        setActiveSlide(slide);
        notifySlideChange(slide);
        smoothScrollToSlide(slide);
        return slide;
      },
      [notifySlideChange, smoothScrollToSlide]
    );

    // useImperativeHandle customizes the instance value that is exposed to parent components when using ref.
    useImperativeHandle(
      ref,
      () => ({
        prev: () => goToSlide(Math.max(0, activeSlide - 1)),
        next: () => goToSlide(Math.min(getLastSlide(), activeSlide + 1)),
        scrollTo: goToSlide
      }),
      [activeSlide, goToSlide, getLastSlide]
    );

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      if (!containerRef.current) {
        return;
      }
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startScrollLeft: containerRef.current.scrollLeft,
        hasMoved: false
      };
      setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!containerRef.current || !isDragging) {
          return;
        }
        const { startX, startScrollLeft } = dragRef.current;
        const delta = startX - e.clientX;
        if (Math.abs(delta) >= DRAG_THRESHOLD_PX) {
          dragRef.current.hasMoved = true;
        }
        containerRef.current.scrollLeft = startScrollLeft + delta;
      },
      [isDragging]
    );

    const handleMouseUp = useCallback(() => setIsDragging(false), []);

    const handleScroll = useCallback(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const visible = getVisibleCount();
      const itemWidth = getItemWidth(el.clientWidth, visible);
      const maxSlide = Math.max(0, totalItems - visible);
      const slide = Math.min(maxSlide, Math.max(0, Math.round(el.scrollLeft / itemWidth)));
      setActiveSlide(slide);
      notifySlideChange(slide);
    }, [getVisibleCount, totalItems, notifySlideChange]);

    const handleClickCapture = useCallback((e: React.MouseEvent) => {
      if (dragRef.current.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current.hasMoved = false;
      }
    }, []);

    useEffect(() => {
      if (!isDragging) {
        return;
      }
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
      <div
        ref={containerRef}
        className={cn(sliderRootClasses, isDragging ? 'cursor-grabbing' : 'cursor-grab', className)}
        style={{
          ...rootStyle,
          scrollBehavior: 'smooth'
        }}
        onMouseDown={handleMouseDown}
        onScroll={handleScroll}
        onClickCapture={handleClickCapture}
      >
        <div data-slider-track className={sliderTrackClasses()}>
          {Children.toArray(children).map((child, index) => (
            <div
              key={index}
              className={sliderItemClasses}
              style={{
                transition: 'transform 300ms ease-in-out'
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

Slider.displayName = 'Slider';
